import React, { useRef, useMemo, useState, useEffect } from 'react'
import debounce from 'lodash/debounce'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { Box, Typography, IconButton } from '@mui/material'
import SearchBarWithRecent from 'components/common/SearchBarWithRecent'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { AgGridReact } from 'ag-grid-react'
import { CustomLoadingOverlay, getStringId, sortColDefs } from 'components/common/AgGridUtils'
import ResetFiltersButton from 'components/common/ResetFiltersButton'
import useWellListContext from 'components/common/hooks/WellListContext'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import CloseIcon from '@mui/icons-material/Close'
import { appColors } from 'utils'
import { Icon as Iconify } from '@iconify/react'

const FavoriteWellsModal = ({ user, onClose }) => {
  const gridApi = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
  const { getAgGridTheme, searchBarStyle, getWindowBarColor } = useInnovaTheme()
  const [filterApplied, setFilterApplied] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [favorites, setFavorites] = useState([])
  const { getDatabaseOrg } = useInnovaAuth()

  const { wellListLoading, wellList } = useWellListContext() // Fetches the well list data

  const fetchObjectsOfInterestRequest = useInnovaAxios({
    url: '/user/getObjectsOfInterest',
  })

  const fetchFavorites = async () => {
    if (!user) return
    if (user === '') return

    setIsLoading(true)

    try {
      const response = await fetchObjectsOfInterestRequest({ user })
      if (response?.error) {
        return
      }

      setFavorites(response.data)
    } finally {
      setIsLoading(false)
    }
  }

  const getFavoriteWells = useMemo(() => {
    const { districts, operators, rigs } = favorites
    if (!Array.isArray(districts) && !Array.isArray(operators) && !Array.isArray(rigs)) return []
    if (districts.length === 0 && operators.length === 0 && rigs.length === 0) return []

    return wellList.filter((well) => {
      return (
        ((Array.isArray(districts) && districts.includes(well.district)) ||
        (Array.isArray(operators) && operators.includes(well.operator)) ||
        (Array.isArray(rigs) && rigs.includes(well.rig))) && 
        well.organization === getDatabaseOrg?.() &&
        well.wellStatus === 'Active'
      )
    })
  }, [favorites, wellList, getDatabaseOrg])

  const debounceSetQuickFilter = debounce((text) => {
    if (gridApi.current) {
      gridApi.current.setGridOption('quickFilterText', text)
    }
  }, 300)

  useEffect(() => {
    debounceSetQuickFilter(searchText)
  }, [searchText]) // eslint-disable-line react-hooks/exhaustive-deps

  const onFirstDataRendered = (params) => {
    if (gridApi.current) gridApi.current.onFilterChanged()
    autoSizeColumns()
  }

  const autoSizeColumns = () => {
    if (gridApi.current === null) {
      return
    }

    gridApi.current?.autoSizeAllColumns()
  }

  const onGridReady = (params) => {
    gridApi.current = params.api
    fetchFavorites()
  }

  const onFilterChanged = (params) => {
    if (!gridApi.current) return
    if (gridApi.current.isDestroyed()) return

    setFilterApplied(Object.keys(gridApi.current.getFilterModel()).length > 0)
  }

  const getRowId = useMemo(() => {
    return (params) => {
      return getStringId(params.data?.actualWell)
    }
  }, [])

  const gridOptions = useMemo(() => {
    return {
      loadingOverlayComponent: CustomLoadingOverlay,
    }
  }, [])

  const centerAlignCell = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    [],
  )

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
      autoHeight: true,
      editable: false,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      cellStyle: centerAlignCell,
      filterParams: {
        excelMode: 'windows',
      },
    }
  }, [centerAlignCell])

  let columnDefs = useMemo(
    () => [
      {
        headerName: '',
        colId: '',
        field: '',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        pinned: 'left',
        lockPosition: 'left',
        cellRenderer: 'agGroupCellRenderer',
        headerComponent: ResetFiltersButton,
        headerComponentParams: {
          filterApplied: filterApplied,
          gridApi: gridApi,
        },
      },
      {
        headerName: 'Well',
        colId: 'actualWell',
        field: 'actualWell',
        pinned: 'left',
      },
      {
        headerName: 'District',
        colId: 'district',
        field: 'district',
      },
      {
        headerName: 'Operator',
        colId: 'operator',
        field: 'operator',
      },
      {
        headerName: 'Rig',
        colId: 'rig',
        field: 'rig',
      },
    ],
    [filterApplied],
  )

  return (
    <React.Fragment>
      <Dialog
        fullWidth={true}
        maxWidth={'xlg'}
        PaperProps={{
          sx: {
            height: '80vh',
            width: '60vw',
            backgroundColor: 'itemBackground',
          },
        }}
        open={true}
        onClose={() => onClose()}>
        <Box
          sx={{
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            color: appColors.headerTextColor,
            backgroundColor: getWindowBarColor(),
            borderBottom: '1px solid rgba(255,255,255,0.12)',
          }}>
          <Typography sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Iconify icon='emojione:star' sx={{ marginRight: '10px' }} />
            Favorite Wells
          </Typography>

          <IconButton onClick={onClose} size='small' sx={{ ml: 1 }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        <Box
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            width: 'calc(100% - 40px)',
            margin: '20px',
          }}>
          <SearchBarWithRecent
            value={searchText}
            onChange={(newSearchTerm) => setSearchText(newSearchTerm)}
            onCancelSearch={() => setSearchText('')}
            style={searchBarStyle()}
            recentKey='alertsHistory'
          />
        </Box>
        <DialogContent>
          <div className={getAgGridTheme()} style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              rowData={getFavoriteWells}
              loading={isLoading || wellListLoading}
              columnDefs={sortColDefs(columnDefs, 'alertsHistory')}
              defaultColDef={defaultColDef}
              headerHeight={30}
              onFirstDataRendered={onFirstDataRendered}
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
              getRowId={getRowId}
              gridOptions={gridOptions}
            />
          </div>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  )
}

export default FavoriteWellsModal
